import { useQueries, useQuery } from '@tanstack/react-query';
import { AxiosPromise } from 'axios';
import _ from 'lodash';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Notification } from 'rsuite';
import { PageOptions } from '../../../@types/FiltersTypes';
import { queryClient } from '../../../App';
import { AlertWithTimestamp } from '../../../components/AlertWithTimestamp';
import {
  getPricerAssignment,
  getPricerAssignmentsForFilter,
  getPricingScopeLevel,
  toResponsibleOption,
} from '../../../pages/Administrador/Produtos/Components/SetupIpaPricingResponsibilities/service';
import { pricingScopeKeys } from '../../../pages/Administrador/Produtos/Components/SetupIpaPricingResponsibilities/hooks/usePricingScopeQueries';
import type { ResponsibleFilterOption } from '../../../pages/Administrador/Produtos/Components/SetupIpaPricingResponsibilities/service';
import type { PricerAssignment } from '../../../pages/Administrador/Produtos/Components/SetupIpaPricingResponsibilities/types';
import { getSavedResponsibleFilter, toSavedResponsibleValues } from '../../../pages/IPA/RevisaoPrecos/Components/SavedFilters/utils';
import {
  CascadingFilterParams,
  getCascadingFilterParams,
  getCascadingFilterParamsSignature,
  hasCascadingFilterParams,
  isCascadingFilterKey,
} from '../../NewFilterBox/data/cascadingFilters';
import {
  applyResponsibleCategories,
  clearResponsibleCategories,
  stripInjectedResponsibleCategories,
  syncInjectedCategoriesAfterManualChange,
} from '../../NewFilterBox/data/responsibleFilter';
import { PostSavedFitersDataProps, deleteSavedFilter, postSavedFilter, putSavedFilter } from '../../../pages/IPA/RevisaoPrecos/services_ts';
import { SAVED_FILTER_ITEM } from '../../../pages/IPA/RevisaoPrecos/utils';
import { extractApiErrorMessage } from '../../../utils/extractApiErrorMessage';
import { listSegmentsData, listaSegmentos } from '../services';
import { useFilterData } from './useFilterData';
import { FilterValueState, useFilterValue } from './useFilterValue';
import { FilterSavedItem, useFilterSaved } from './useFilterSaved';
import { useModal } from './useModal';

type IDataItem = {
  label: string;
  value: string;
} & Record<string, unknown>;

export type IFilterItem = {
  key: string;
  queryFn?: (query: string, options?: CascadingFilterParams) => Promise<Record<'label' | 'value', string>[]>;
  selectAll?: boolean;
  type?: 'check' | 'select';
  placeholder?: ReactNode;
  textSearch?: string;
  textLoading?: string;
} & Record<string, unknown>;

export type IUseFilterOptions = {
  page: PageOptions;
  filters: IFilterItem[];
  enableCascadingFilters?: boolean;
};

export type IUseFilter = {
  data: Partial<Record<string, IDataItem[]>>;
  values: Partial<Record<string, string[]>>;
  filters: IFilterItem[];
  savedFilter: SAVED_FILTER_ITEM;
  modals: Partial<Record<string, boolean>>;
  isEmpty: boolean;
  loading: Partial<Record<string, boolean>>;
  onSearch: (key: string, query: string) => void;
  onSelect: (key: string, value: string[] | string | null) => void;
  onClearAll: () => void;
  onSelectAll: (key: string, checked: boolean) => void;
  onOpen: (key: string) => void | Promise<void>;
  onOpenModal: (key: string) => void;
  onCloseModal: (key: string) => void;
  onSaveFilter: (data: SAVED_FILTER_ITEM, option: 'edit' | 'save') => Promise<void>;
  onDeleteFilter: (id: string) => Promise<AxiosPromise<any>>;
  onSelectSavedFilter: (item: SAVED_FILTER_ITEM) => void;
};

/** Os valores já com o que os efeitos do gancho injetaram no commit corrente (ex.: categorias do Responsável). */
export type IUseFilterSection = IUseFilter & { readValues: () => FilterValueState };

const index = {
  save: {
    queryFn: postSavedFilter,
    description: 'Filtro salvo com sucesso.',
  },
  edit: {
    queryFn: putSavedFilter,
    description: 'Filtro editado com sucesso.',
  },
  exclude: {
    queryFn: deleteSavedFilter,
    description: 'Filtro excluido com sucesso.',
  },
} as const;

const queryKey = 'get-filter-data-';
const RESPONSIBLE_FILTER_KEY = 'responsible';
const isCategoryLevelKey = (key: string): boolean => /^categoryLevel[1-7]$/.test(key);

const isValidPricingScopeLevel = (level?: number | null): level is number => typeof level === 'number' && Number.isInteger(level) && level >= 1 && level <= 7;

export const useFilterSection = ({ page, filters, enableCascadingFilters = false }: IUseFilterOptions): IUseFilterSection => {
  const [data, { add: addData, set: setData }] = useFilterData();

  const [cache, { reset: resetCache, set: setCache }] = useFilterData();

  const [values, { set: setValues, reset: resetValues, resetByKey: resetValuesByKey }] = useFilterValue();

  const [modals, { onOpen: onOpenModal, onHide: onCloseModal }] = useModal();

  const [savedFilter, { onClean: onCleanSavedFilter, onSelect: selectSavedFilter, onSet: setSavedFilter }] = useFilterSaved();

  const [search, setSearch] = useState<Partial<Record<string, string>>>({});

  const [filtersWithSegments, setUpdatedFilterList] = useState<IFilterItem[]>(filters);

  const timer = useRef<NodeJS.Timeout>();
  const valuesRef = useRef<FilterValueState>(values);
  const initialCascadingDataRef = useRef<Partial<Record<string, IDataItem[]>>>({});
  const lastLoadedCascadingSignatureRef = useRef<Partial<Record<string, string>>>({});
  const responsibleAssignmentsRef = useRef(new Map<string, PricerAssignment>());
  const responsibleIdRef = useRef<string | null>(null);
  const responsibleInjectedCategoriesRef = useRef<string[]>([]);
  const responsibleCategoryLevelRef = useRef<string | null>(null);
  const responsibleSavedManualCategoriesRef = useRef<Partial<Record<string, string[]>>>({});

  const hasResponsibleFilter = filters.some(({ key }) => key === RESPONSIBLE_FILTER_KEY);
  const responsibleSearch = search[RESPONSIBLE_FILTER_KEY] ?? '';
  const responsibleAssignmentsParams = useMemo(() => ({ search: responsibleSearch, page: 0, size: 20, sort: 'userEmail,asc' }), [responsibleSearch]);

  const { data: pricingScopeLevel, fetchStatus: loadingPricingScopeLevel } = useQuery({
    queryKey: pricingScopeKeys.level(),
    queryFn: getPricingScopeLevel,
    enabled: hasResponsibleFilter,
  });

  const hasValidPricingScopeLevel = isValidPricingScopeLevel(pricingScopeLevel?.level);

  const { data: responsibleAssignments, fetchStatus: loadingResponsibleAssignments } = useQuery({
    queryKey: pricingScopeKeys.assignments(responsibleAssignmentsParams),
    queryFn: () => getPricerAssignmentsForFilter(responsibleSearch),
    enabled: hasResponsibleFilter && hasValidPricingScopeLevel,
    keepPreviousData: true,
  });

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    responsibleAssignments?.forEach(assignment => {
      responsibleAssignmentsRef.current.set(assignment.id, assignment);
    });
  }, [responsibleAssignments]);

  useEffect(() => {
    const responsibleId = values[RESPONSIBLE_FILTER_KEY]?.[0];

    if (!responsibleId) {
      responsibleIdRef.current = null;
      responsibleInjectedCategoriesRef.current = [];
      responsibleCategoryLevelRef.current = null;
      responsibleSavedManualCategoriesRef.current = {};
      return;
    }

    if (responsibleIdRef.current !== responsibleId) {
      responsibleIdRef.current = responsibleId;
      responsibleInjectedCategoriesRef.current = [];
      responsibleCategoryLevelRef.current = null;
    }

    const responsibleOption = (cache[RESPONSIBLE_FILTER_KEY] ?? []).find(item => item.value === responsibleId) as ResponsibleFilterOption | undefined;
    if (!responsibleOption?.categoryLevel) {
      const assignment = responsibleAssignmentsRef.current.get(responsibleId);
      const level = pricingScopeLevel?.level;

      if (!assignment || !isValidPricingScopeLevel(level)) return;

      const categoryLevel = `categoryLevel${level}`;
      const currentCategoryValues = responsibleSavedManualCategoriesRef.current[categoryLevel] ?? (values[categoryLevel] as string[] | undefined) ?? [];
      const projection = applyResponsibleCategories(currentCategoryValues, [], assignment.categories ?? []);
      const assignedOptions = (assignment.categories ?? []).map(category => ({ label: category, value: category }));
      const categoryData = data[categoryLevel] ?? [];
      const categoryCache = cache[categoryLevel] ?? [];
      const mergedCategoryData = _.uniqBy([...categoryData, ...categoryCache, ...assignedOptions], 'value');
      const categoryOptionsByValue = new Map(mergedCategoryData.map(option => [option.value, option]));
      const selectedCategoryCache = projection.values.map(value => categoryOptionsByValue.get(value) ?? { label: value, value });
      const visibleCategoryData = _.uniqBy([...selectedCategoryCache, ...mergedCategoryData], 'value');

      setData(categoryLevel, visibleCategoryData);
      setCache(categoryLevel, selectedCategoryCache);
      setValues(categoryLevel, projection.values);
      valuesRef.current = {
        ...valuesRef.current,
        [categoryLevel]: projection.values,
      };

      const hydratedOption = {
        ...(responsibleOption ?? toResponsibleOption(assignment)),
        categoryLevel,
        injectedCategories: projection.injected,
      };

      setCache(RESPONSIBLE_FILTER_KEY, [hydratedOption]);
      responsibleCategoryLevelRef.current = categoryLevel;
      responsibleInjectedCategoriesRef.current = projection.injected;
      return;
    }

    responsibleCategoryLevelRef.current = responsibleOption.categoryLevel;
    responsibleInjectedCategoriesRef.current = responsibleOption.injectedCategories ?? [];
  }, [cache, data, pricingScopeLevel, responsibleAssignments, setCache, setData, setValues, values]);

  const getFilterCascadingParams = (key: string) => {
    if (!enableCascadingFilters || !isCascadingFilterKey(key)) return {};
    return getCascadingFilterParams(valuesRef.current, key);
  };

  const onSearch = (key: string, query: string) => {
    clearTimeout(timer.current);
    const newTimer = setTimeout(() => {
      setSearch(oldValue => ({ ...oldValue, [key]: query }));
    }, 500);
    timer.current = newTimer;
  };

  const performClearResponsible = () => {
    const categoryLevel = responsibleCategoryLevelRef.current;
    const injectedCategories = responsibleInjectedCategoriesRef.current;
    const nextValues = { ...valuesRef.current };

    if (categoryLevel && injectedCategories.length) {
      const currentCategoryValues = (valuesRef.current[categoryLevel] as string[] | undefined) ?? [];
      const nextCategoryValues = clearResponsibleCategories(currentCategoryValues, injectedCategories);
      nextValues[categoryLevel] = nextCategoryValues;
      setValues(categoryLevel, nextCategoryValues);
    }

    nextValues[RESPONSIBLE_FILTER_KEY] = [];
    valuesRef.current = nextValues;
    setValues(RESPONSIBLE_FILTER_KEY, []);
    setCache(RESPONSIBLE_FILTER_KEY, []);

    if (search[RESPONSIBLE_FILTER_KEY]) {
      setSearch(oldValue => ({ ...oldValue, [RESPONSIBLE_FILTER_KEY]: '' }));
    }

    responsibleIdRef.current = null;
    responsibleInjectedCategoriesRef.current = [];
    responsibleCategoryLevelRef.current = null;
    responsibleSavedManualCategoriesRef.current = {};
  };

  const hydrateAndClearResponsible = async (responsibleId: string) => {
    try {
      const assignment = responsibleAssignmentsRef.current.get(responsibleId) ?? (await getPricerAssignment(responsibleId));
      let level = pricingScopeLevel?.level;

      if (!isValidPricingScopeLevel(level)) {
        level = (await getPricingScopeLevel())?.level;
      }

      if (!assignment || !isValidPricingScopeLevel(level)) {
        throw new Error('Não foi possível hidratar o responsável salvo');
      }

      if (valuesRef.current[RESPONSIBLE_FILTER_KEY]?.[0] !== responsibleId) return;

      responsibleAssignmentsRef.current.set(responsibleId, assignment);
      const categoryLevel = `categoryLevel${level}`;
      const savedManualCategoryValues = responsibleSavedManualCategoriesRef.current[categoryLevel];
      const currentCategoryValues = savedManualCategoryValues ?? (valuesRef.current[categoryLevel] as string[] | undefined) ?? [];
      const injectedCategories = applyResponsibleCategories(currentCategoryValues, [], assignment.categories ?? []).injected;
      const responsibleOption = (cache[RESPONSIBLE_FILTER_KEY] ?? []).find(item => item.value === responsibleId) as ResponsibleFilterOption | undefined;

      setCache(RESPONSIBLE_FILTER_KEY, [
        {
          ...(responsibleOption ?? toResponsibleOption(assignment)),
          categoryLevel,
          injectedCategories,
        },
      ]);
      responsibleIdRef.current = responsibleId;
      responsibleCategoryLevelRef.current = categoryLevel;
      responsibleInjectedCategoriesRef.current = injectedCategories;
      performClearResponsible();
    } catch (error) {
      if (valuesRef.current[RESPONSIBLE_FILTER_KEY]?.[0] !== responsibleId) return;

      AlertWithTimestamp({
        message: extractApiErrorMessage(error) ?? 'Não foi possível limpar o responsável. Tente novamente.',
      });
    }
  };

  const clearResponsible = () => {
    const responsibleId = valuesRef.current[RESPONSIBLE_FILTER_KEY]?.[0];

    if (responsibleId && (responsibleIdRef.current !== responsibleId || !responsibleCategoryLevelRef.current)) {
      void hydrateAndClearResponsible(responsibleId);
      return;
    }

    performClearResponsible();
  };

  const selectResponsible = (value: string) => {
    const assignment = responsibleAssignmentsRef.current.get(value);
    const level = pricingScopeLevel?.level;

    if (!assignment || !isValidPricingScopeLevel(level)) return;

    responsibleSavedManualCategoriesRef.current = {};

    const categoryLevel = `categoryLevel${level}`;
    const previousCategoryLevel = responsibleCategoryLevelRef.current;
    const previousInjectedCategories = responsibleInjectedCategoriesRef.current;
    const nextValues = { ...valuesRef.current };
    let currentCategoryValues = (nextValues[categoryLevel] as string[] | undefined) ?? [];

    if (previousCategoryLevel) {
      const previousCategoryValues = (nextValues[previousCategoryLevel] as string[] | undefined) ?? [];
      const cleanedPreviousValues = clearResponsibleCategories(previousCategoryValues, previousInjectedCategories);
      nextValues[previousCategoryLevel] = cleanedPreviousValues;
      setValues(previousCategoryLevel, cleanedPreviousValues);

      if (previousCategoryLevel === categoryLevel) {
        currentCategoryValues = cleanedPreviousValues;
      }
    }

    const projection = applyResponsibleCategories(
      currentCategoryValues,
      previousCategoryLevel === categoryLevel ? previousInjectedCategories : [],
      assignment.categories ?? [],
    );

    const assignedOptions = (assignment.categories ?? []).map(category => ({ label: category, value: category }));
    const categoryData = data[categoryLevel] ?? [];
    const categoryCache = cache[categoryLevel] ?? [];
    const mergedCategoryData = _.uniqBy([...categoryData, ...categoryCache, ...assignedOptions], 'value');
    const categoryOptionsByValue = new Map(mergedCategoryData.map(option => [option.value, option]));
    const selectedCategoryCache = projection.values.map(value => categoryOptionsByValue.get(value) ?? { label: value, value });
    const visibleCategoryData = _.uniqBy([...selectedCategoryCache, ...mergedCategoryData], 'value');

    setData(categoryLevel, visibleCategoryData);
    setCache(categoryLevel, selectedCategoryCache);
    setValues(categoryLevel, projection.values);
    setValues(RESPONSIBLE_FILTER_KEY, [value]);
    setCache(RESPONSIBLE_FILTER_KEY, [
      {
        ...toResponsibleOption(assignment),
        categoryLevel,
        injectedCategories: projection.injected,
      },
    ]);

    valuesRef.current = {
      ...nextValues,
      [categoryLevel]: projection.values,
      [RESPONSIBLE_FILTER_KEY]: [value],
    };
    responsibleIdRef.current = value;
    responsibleInjectedCategoriesRef.current = projection.injected;
    responsibleCategoryLevelRef.current = categoryLevel;
  };

  const onSelect = (key: string, value: string[] | string | null) => {
    const nextValue = Array.isArray(value) ? value : value ? [value] : [];

    if (key === RESPONSIBLE_FILTER_KEY) {
      if (nextValue[0]) selectResponsible(nextValue[0]);
      else clearResponsible();
      return;
    }

    valuesRef.current = {
      ...valuesRef.current,
      [key]: nextValue,
    };
    setValues(key, nextValue);
    const updatedList = data[key]?.filter(item => nextValue.includes(item.value)) ?? [];
    setCache(key, updatedList);

    const responsibleId = valuesRef.current[RESPONSIBLE_FILTER_KEY]?.[0];
    if (responsibleId && isCategoryLevelKey(key)) {
      responsibleSavedManualCategoriesRef.current[key] = nextValue;
    }

    if (responsibleCategoryLevelRef.current !== key) return;

    responsibleInjectedCategoriesRef.current = syncInjectedCategoriesAfterManualChange(nextValue, responsibleInjectedCategoriesRef.current);

    const responsibleCache = cache[RESPONSIBLE_FILTER_KEY] ?? [];
    if (!responsibleId || !responsibleCache.some(item => item.value === responsibleId)) return;

    setCache(
      RESPONSIBLE_FILTER_KEY,
      responsibleCache.map(item => (item.value === responsibleId ? { ...item, injectedCategories: responsibleInjectedCategoriesRef.current } : item)),
    );
  };

  const onClearAll = () => {
    onCleanSavedFilter();
    valuesRef.current = {};
    resetCache();
    resetValues();
    if (hasResponsibleFilter) {
      setSearch(oldValue => ({ ...oldValue, [RESPONSIBLE_FILTER_KEY]: '' }));
      responsibleIdRef.current = null;
      responsibleInjectedCategoriesRef.current = [];
      responsibleCategoryLevelRef.current = null;
    }
    responsibleSavedManualCategoriesRef.current = {};
  };

  const onSelectAll = (key: string, checked: boolean) => {
    if (!checked) {
      const nextValues = { ...valuesRef.current };
      delete nextValues[key];
      valuesRef.current = nextValues;
      return resetValuesByKey(key);
    }
    const allData = data[key]?.map(({ value }) => value) ?? [];
    valuesRef.current = {
      ...valuesRef.current,
      [key]: allData,
    };
    return setValues(key, allData);
  };

  const onSelectSavedFilter = (item: FilterSavedItem) => {
    onClearAll();
    selectSavedFilter(item);

    const { filters } = item;
    responsibleSavedManualCategoriesRef.current = Object.keys(filters).reduce<Partial<Record<string, string[]>>>((manualCategories, key) => {
      if (isCategoryLevelKey(key) && Array.isArray(filters[key as keyof typeof filters])) {
        manualCategories[key] = (filters[key as keyof typeof filters] as unknown[]).filter((value): value is string => typeof value === 'string');
      }

      return manualCategories;
    }, {});

    Object.keys(filters).forEach(k => {
      const _k = k as keyof typeof filters;

      const isIgnoredKeys = _k.match(/productIds/) || _k.match(/storeIds/) || _k.match(/savedFilters/);

      if (isIgnoredKeys) return;

      const currentFilter = filters[_k] as any[];

      if (!currentFilter) return;

      if (_k === RESPONSIBLE_FILTER_KEY) {
        const { value, cache: responsibleCache } = getSavedResponsibleFilter(currentFilter);
        responsibleCache.forEach(option => addData(RESPONSIBLE_FILTER_KEY, option));
        setCache(RESPONSIBLE_FILTER_KEY, responsibleCache);
        valuesRef.current = {
          ...valuesRef.current,
          [RESPONSIBLE_FILTER_KEY]: value,
        };
        setValues(RESPONSIBLE_FILTER_KEY, value);

        const savedResponsible = responsibleCache[0];
        responsibleIdRef.current = value[0] ?? null;
        responsibleCategoryLevelRef.current = savedResponsible?.categoryLevel ?? null;
        responsibleInjectedCategoriesRef.current = savedResponsible?.injectedCategories ?? [];
        return;
      }

      if (_k.match(/products/)) {
        currentFilter.forEach(filter =>
          addData('productIds', {
            label: filter.description,
            value: filter.id,
          }),
        );
        const values = currentFilter.map(filter => filter.id);
        valuesRef.current = {
          ...valuesRef.current,
          productIds: values,
        };
        setValues('productIds', values);
        return;
      }

      if (_k.match(/stores/)) {
        currentFilter.forEach(filter =>
          addData('productIds', {
            label: filter.description,
            value: filter.id,
          }),
        );
        const values = currentFilter.map(filter => filter.id);
        valuesRef.current = {
          ...valuesRef.current,
          storeIds: values,
        };
        setValues('storeIds', values);
        return;
      }

      if (_k.match(/clusters/)) {
        currentFilter.forEach(({ label, id }) => addData('clusters', { label, value: id }));
        const values = currentFilter.map(filter => filter.id);
        valuesRef.current = {
          ...valuesRef.current,
          clusters: values,
        };
        setValues('clusters', values);
        return;
      }

      currentFilter.forEach(item =>
        addData(_k, {
          label: item,
          value: item,
        }),
      );

      valuesRef.current = {
        ...valuesRef.current,
        [_k]: currentFilter,
      };
      setValues(_k, currentFilter);
    });
  };

  const isEmpty = (() => {
    let isEmpty = true;
    Object.values(values).forEach(v => {
      if (Array.isArray(v) && v.length > 0) {
        isEmpty = false;
      }
    });
    return isEmpty;
  })();

  const responsibleCache = cache[RESPONSIBLE_FILTER_KEY];
  const responsibleOptions = useMemo<IDataItem[]>(() => {
    if (!hasResponsibleFilter) return [];

    if (!hasValidPricingScopeLevel) return responsibleCache ?? [];

    return _.uniqBy([...(responsibleCache ?? []), ...(responsibleAssignments ?? []).map(toResponsibleOption)], 'value');
  }, [hasResponsibleFilter, hasValidPricingScopeLevel, responsibleAssignments, responsibleCache]);

  const visibleData = useMemo(
    () => (hasResponsibleFilter ? { ...data, [RESPONSIBLE_FILTER_KEY]: responsibleOptions } : data),
    [data, hasResponsibleFilter, responsibleOptions],
  );

  const queryFilters = useMemo(() => filtersWithSegments.filter(({ key }) => key !== RESPONSIBLE_FILTER_KEY), [filtersWithSegments]);

  const loading = (() => {
    const loadingObject: IUseFilter['loading'] = {};
    Object.keys(data).forEach(key => {
      loadingObject[key] = !!queryClient.isFetching([queryKey + key]);
    });
    if (hasResponsibleFilter) {
      loadingObject[RESPONSIBLE_FILTER_KEY] = loadingPricingScopeLevel === 'fetching' || loadingResponsibleAssignments === 'fetching';
    }
    return loadingObject;
  })();

  const results = useQueries({
    queries: queryFilters.map(({ key, queryFn }) => {
      const cascadingParams = getFilterCascadingParams(key);

      return {
        queryKey: [queryKey + key, search[key]],
        queryFn: () => (enableCascadingFilters && isCascadingFilterKey(key) ? queryFn?.(search[key] ?? '', cascadingParams) : queryFn?.(search[key] ?? '')),
        onSuccess: (data: IDataItem[]) => {
          const newData = _.unionBy([...(cache[key] ?? []), ...(data ?? [])], 'value');

          if (enableCascadingFilters && isCascadingFilterKey(key)) {
            if (!search[key] && !hasCascadingFilterParams(cascadingParams)) {
              initialCascadingDataRef.current[key] = newData;
            }

            if (!search[key]) {
              lastLoadedCascadingSignatureRef.current[key] = getCascadingFilterParamsSignature(cascadingParams);
            }
          }

          setData(key, newData);
        },
        retry: false,
      };
    }),
  });

  const refetchByKey = async (key: string): Promise<IFilterItem[]> => {
    const queryKeyToRefetch = [queryKey + key, search[key]];
    await queryClient.invalidateQueries(queryKeyToRefetch);
    const data = (await queryClient.getQueryData(queryKeyToRefetch)) as IFilterItem[];
    return data;
  };

  const onOpen = async (key: string) => {
    if (!enableCascadingFilters || !isCascadingFilterKey(key)) return;

    const cascadingParams = getFilterCascadingParams(key);
    const cascadingSignature = getCascadingFilterParamsSignature(cascadingParams);

    if (hasCascadingFilterParams(cascadingParams)) {
      if (!search[key] && lastLoadedCascadingSignatureRef.current[key] === cascadingSignature) return;
      const filterIndex = queryFilters.findIndex(filter => filter.key === key);
      await results[filterIndex]?.refetch();
      return;
    }

    if (!search[key] && initialCascadingDataRef.current[key]) {
      lastLoadedCascadingSignatureRef.current[key] = cascadingSignature;
      setData(key, initialCascadingDataRef.current[key] ?? []);
    }
  };

  const onSuccess = (modal: string, option: keyof typeof index) => {
    onCloseModal(modal);
    Notification.success({
      description: index[option].description,
    });
  };

  const handleUpdateSavedFilterAfterSave = async (name: string) => {
    let resultValue = {};
    refetchByKey('savedFilters').then(data => {
      if (data) {
        resultValue = data?.find(item => item.label === name) ?? {};
        setSavedFilter(resultValue as SAVED_FILTER_ITEM);
      }
    });
  };

  const onSaveFilter = async ({ name, value }: SAVED_FILTER_ITEM, option: 'edit' | 'save'): Promise<void> => {
    try {
      const savedValues = hasResponsibleFilter
        ? {
            ...stripInjectedResponsibleCategories(
              values as Record<string, unknown>,
              responsibleCategoryLevelRef.current,
              responsibleInjectedCategoriesRef.current,
            ),
            [RESPONSIBLE_FILTER_KEY]: toSavedResponsibleValues(
              values[RESPONSIBLE_FILTER_KEY] ?? [],
              (cache[RESPONSIBLE_FILTER_KEY] ?? []) as ResponsibleFilterOption[],
            ),
          }
        : values;
      const model: PostSavedFitersDataProps<Record<string, unknown>> = {
        screen: page,
        name,
        id: value,
        filters: savedValues as Record<string, unknown>,
      };
      const res = await index[option].queryFn(model);
      if (res.status === 200 || res.status === 201) {
        onSuccess('salvarFiltros', option);
        handleUpdateSavedFilterAfterSave(name);
      }
    } catch (error) {
      AlertWithTimestamp({
        message: extractApiErrorMessage(error) ?? 'Erro ao salvar filtro',
      });
    }
  };

  const onDeleteFilter = async (id: string): Promise<AxiosPromise<any>> => {
    const res = await deleteSavedFilter(id);
    if (res.status === 200) {
      onSuccess('excluirFiltro', 'exclude');
      onClearAll();
      refetchByKey('savedFilters');
    }
    return res;
  };

  useQuery({
    queryKey: ['get-segmentos'],
    initialData: [],
    queryFn: listaSegmentos,
    onSuccess: data => {
      const updatedList: IFilterItem[] = [];
      data.forEach(({ field, name, level }) => {
        const data: IFilterItem = {
          key: field,
          placeholder: name,
          level,
          textLoading: 'Carregando produtos...',
          textSearch: `Buscar por ${name}`,
          queryFn: (query, options) => listSegmentsData(field, { query, ...options }),
        };
        updatedList.push(data);
      });
      setUpdatedFilterList([...filters, ...updatedList]);
    },
  });

  return {
    data: visibleData,
    values: useMemo(() => values, [values]),
    filters: filtersWithSegments,
    modals,
    savedFilter,
    isEmpty,
    loading,
    onSearch,
    onSelect,
    onClearAll,
    onSelectAll,
    onOpen,
    onOpenModal,
    onCloseModal,
    onSaveFilter,
    onDeleteFilter,
    onSelectSavedFilter,
    readValues: () => valuesRef.current,
  };
};
